import "./RangeSlider.styles.css";

import {
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import type { RangeSliderProps } from "./RangeSlider.types";

const RangeSlider = forwardRef<
  HTMLInputElement,
  PropsWithChildren<RangeSliderProps>
>(({ min, max, minValue, maxValue, onChange }, forwardedRef) => {
  const [minVal, setMinVal] = useState(minValue);
  const [maxVal, setMaxVal] = useState(maxValue);

  const range = useRef<HTMLInputElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minVal, getPercent]);

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxVal, getPercent]);

  useEffect(() => {
    if (typeof onChange !== "function") return;
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  return (
    <div className="relative flex w-[400px] max-w-full items-center justify-center screen850:max-w-none">
      <input
        type="range"
        ref={forwardedRef}
        min={min}
        max={max}
        value={minVal}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal);
          setMinVal(value);
        }}
        className="thumb z-[3]"
        style={{ zIndex: minVal > max - 100 ? "5" : undefined }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal);
          setMaxVal(value);
        }}
        className="thumb z-[4]"
      />

      <div className="relative w-full">
        <div className="absolute z-[1] h-1 w-full rounded bg-neutral-500" />
        <div
          ref={range}
          className="absolute z-[2] h-1 rounded bg-primary-500"
        />
      </div>
    </div>
  );
});

RangeSlider.displayName = "RangeSlider";

export default RangeSlider;
